import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';

import { isEqual } from 'underscore';
import { cloneDeep } from 'lodash';

import { ControlValueAccessorModel, getProvider } from '../../../../Common/Forms/models/control-value-accessor.model';
import { IncludeExcludeModel } from '../../../../Common/Misc/models/include-exclude-model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { IncludeOrExclude } from '../../../../Common/Dialogs/services/data-select-dialog.service.interface';
import { AutocompleteBrowseAllOptions } from '../../../../Common/RhpContexts/components/ContactEditor/SubjectChips/subject-chips.component';
import { JournodexSubjectsService } from '../../../../Common/Journodex/services/journodex-subjects.service';
import { TargetingSearchesService } from '../../../../Discover/Targeting/services/targeting-searches.service';

export interface IncludeExcludeBrowseAllOptions {
    includeOrExclude: IncludeOrExclude;
    options?: AutocompleteBrowseAllOptions;
}

@Component({
    selector: 'agpr-include-exclude-subject-chips',
    templateUrl: './include-exclude-subject-chips.component.html',
    styleUrls: ['../../../../Common/Forms/components/IncludeExcludeChips/include.exclude.chips.component.scss'],
    providers: [getProvider(IncludeExcludeSubjectChipsComponent)],
    animations: [
        trigger('containerState', [
            state('hidden', style({
                opacity: 0,
                height: 0
            })),
            state('visible', style({
                opacity: 1,
                height: '*'
            })),
            transition('visible <=> hidden', animate('250ms ease-in'))
        ]),
        trigger('labelState', [
            state('visible', style({
                opacity: 1,
                flex: 1,
                width: '45px',
                minWidth: '45px',
                maxWidth: '45px'
            })),
            state('hidden', style({
                opacity: 0,
                flex: 0,
                width: 0
            })),
            transition('hidden <=> visible', animate('250ms ease-in'))
        ])
    ],
    standalone: false
})
export class IncludeExcludeSubjectChipsComponent<T> extends ControlValueAccessorModel<IncludeExcludeModel<T>> implements OnInit {

    @Input() id: string = null;
    @Input() disabled: boolean = false;
    @Input() tabindex: number = null;
    @Input() headerLabel: string = null;
    @Input() placeholder: string = '';
    @Input() tooltip: string = null;

    @Output() browseAll = new EventEmitter<IncludeExcludeBrowseAllOptions>();

    showBrowseAll: boolean;
    expanded: boolean = false;

    readonly includeOrExclude = IncludeOrExclude;
    readonly buttonTypes = ButtonTypesEnum;

    constructor(
        public targetingSearchesService: TargetingSearchesService,
        public journodexSubjectsService: JournodexSubjectsService
    ) {
        super();
    }

    ngOnInit(): void {
        if (!this.value) {
            this.value = new IncludeExcludeModel();

        } else {
            this.checkShouldExpand();
        }

        this.showBrowseAll = this.browseAll.observers.length > 0;
    }

    handleChanges(property: IncludeOrExclude, value: T[]): void {
        if (this.value && this.value[property] && !isEqual(this.value[property], value)) {
            this.value[property] = cloneDeep(value);
            this.onModelChange(this.value);
        }

        this.checkShouldExpand();
    }

    checkShouldExpand(): void {
        if (this.value && (this.value.exclude.length > 0 || this.value.includeAll?.length > 0)) {
            this.expanded = true;
        }
    }

    toggleExpanded(): void {
        // Closes any panels that are open before toggling
        document.body.click();
        this.expanded = !this.expanded;
    }
}
